/**
 * The game loop (architecture §2.3): fixed 60 Hz logic ticks + a continuous presentation instant, one rAF callback.
 *
 * Per frame: `clock.advance(cadence ? 1000/hz × jitter : realDt)` → logic ticks until `simMs` covers `presentMs`
 * (capped at 5 unless stepped; backlog dropped = `slowFrames++`) → presentation at the exact instant → renderer frame →
 * `input.endFrame()`. `?cadence=<hz>` (+ seeded `?jitter=`) replaces real time with an emulated display cadence.
 *
 * The power policy (GDD §10.1.7) skips rAF callbacks on idle non-battle screens (≤ 30 fps after 8 s without input);
 * `?power=max` disables it. Hidden tabs: rAF stops by itself; on return `resetTiming()` drops the gap.
 */
import { Rng, runLogicFrame, type Clock } from '../shared';

export interface LoopHooks {
  /** One logic tick (SIM_DT) — mounted screens' `update`. */
  tick(dtMs: number): void;
  /** Presentation at the exact instant — screens' `render`, then the renderer frame. */
  render(presentMs: number, realDtMs: number): void;
  /** After presentation: input bookkeeping (`markFrame` + `endFrame`). */
  endFrame(rafTs: number, presentMs: number): void;
}

export interface LoopOptions {
  /** Defaults to `requestAnimationFrame` (tests inject a manual scheduler). */
  raf?: (cb: (ts: number) => void) => unknown;
  /** Current power cap in fps (null = uncapped); queried every rAF. */
  powerCap?: () => number | null;
}

export interface LoopStats {
  frames: number;
  /** Presented frames in the last second. */
  fps: number;
  cpuMsAvg: number;
  cpuMsP95: number;
  slowFrames: number;
  /** Median presented-frame interval of the last 60 frames (creatures §C3.10 hit-stop floor). */
  frameMsMedian: number;
  cadenceHz: number | null;
  jitter: number;
}

const WINDOW = 120;

export class Loop {
  private running = false;
  private lastTs: number | null = null;
  private lastProcessedTs = -Infinity;
  private cadenceHz: number | null = null;
  private jitter = 0;
  private jitterRng = new Rng('cadence');
  private readonly cpu: number[] = [];
  private readonly intervals: number[] = [];
  private readonly frameTimes: number[] = [];
  private framesTotal = 0;
  private slow = 0;
  private readonly raf: (cb: (ts: number) => void) => unknown;

  constructor(private readonly clock: Clock, private readonly hooks: LoopHooks, private readonly opts: LoopOptions = {}) {
    this.raf = opts.raf ?? ((cb) => requestAnimationFrame(cb));
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTs = null;
    this.raf(this.onRaf);
  }

  stop(): void { this.running = false; }
  get isRunning(): boolean { return this.running; }

  /** `?cadence=` / `__marg.setCadence`: emulated display cadence (null = real time), seeded ± jitter (0–0.3). */
  setCadence(hz: number | null, jitter = 0, seed = 'cadence'): void {
    this.cadenceHz = hz !== null && hz > 0 ? hz : null;
    this.jitter = Math.min(0.3, Math.max(0, jitter));
    this.jitterRng = new Rng(`${seed}:jitter`);
  }

  /** After a hidden tab or a long pause in rAF delivery: the next frame uses dt = 0. */
  resetTiming(): void { this.lastTs = null; }

  /** The dt this frame advances presentation by (before `timeScale`). */
  private frameDt(realDt: number): number {
    if (this.cadenceHz === null) return realDt;
    const base = 1000 / this.cadenceHz;
    if (this.jitter <= 0) return base;
    return base * (1 + this.jitter * (2 * this.jitterRng.float() - 1));
  }

  private readonly onRaf = (ts: number): void => {
    if (!this.running) return;
    this.raf(this.onRaf);
    const cap = this.opts.powerCap?.() ?? null;
    if (cap !== null && cap > 0 && ts - this.lastProcessedTs < 1000 / cap - 2) return;   // power policy: skip this callback
    this.lastProcessedTs = ts;
    this.frame(ts);
  };

  /** One frame (public for tests and for `__marg.step`, which renders the stepped instant immediately). */
  frame(ts: number): void {
    const t0 = typeof performance !== 'undefined' ? performance.now() : 0;
    const realDt = this.lastTs === null ? 0 : Math.max(0, ts - this.lastTs);
    if (this.lastTs !== null) {
      this.intervals.push(realDt);
      if (this.intervals.length > 60) this.intervals.shift();
    }
    this.lastTs = ts;
    const dt = realDt === 0 && this.cadenceHz === null ? 0 : this.frameDt(realDt);
    const r = runLogicFrame(this.clock, dt, (step) => this.hooks.tick(step));
    if (r.slow) this.slow++;
    this.hooks.render(this.clock.presentMs, realDt);
    this.hooks.endFrame(ts, this.clock.presentMs);
    this.framesTotal++;
    this.frameTimes.push(ts);
    while (this.frameTimes.length > 0 && ts - this.frameTimes[0] > 1000) this.frameTimes.shift();
    if (typeof performance !== 'undefined') {
      this.cpu.push(performance.now() - t0);
      if (this.cpu.length > WINDOW) this.cpu.shift();
    }
  }

  stats(): LoopStats {
    const sorted = [...this.cpu].sort((a, b) => a - b);
    const avg = sorted.length ? sorted.reduce((s, x) => s + x, 0) / sorted.length : 0;
    const p95 = sorted.length ? sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95))] : 0;
    const iv = [...this.intervals].sort((a, b) => a - b);
    return {
      frames: this.framesTotal,
      fps: this.frameTimes.length,
      cpuMsAvg: avg,
      cpuMsP95: p95,
      slowFrames: this.slow,
      frameMsMedian: iv.length ? iv[Math.floor(iv.length / 2)] : 1000 / 60,
      cadenceHz: this.cadenceHz,
      jitter: this.jitter,
    };
  }
}

/** Screen kinds that are never power-capped (battles, Облик, cinematics, the card match — GDD §10.1.7). */
const UNCAPPED_KINDS = new Set(['immersive', 'stage', 'debug', 'boot']);

/**
 * GDD §10.1.7 power policy: on non-battle screens, after 8 s without input → ≤ 30 fps; any input restores the ceiling
 * within one frame; `power=max` disables the cap (captures).
 */
export class PowerPolicy {
  static readonly IDLE_MS = 8000;
  static readonly IDLE_FPS = 30;
  private lastInput = 0;
  constructor(private readonly mode: 'auto' | 'max', private readonly now: () => number) { this.lastInput = now(); }
  noteInput(): void { this.lastInput = this.now(); }
  /** Cap for the given top screen kind (null = uncapped). */
  cap(kind: string | undefined): number | null {
    if (this.mode === 'max') return null;
    if (kind && UNCAPPED_KINDS.has(kind)) return null;
    return this.now() - this.lastInput >= PowerPolicy.IDLE_MS ? PowerPolicy.IDLE_FPS : null;
  }
}
